import type { RecommendedQuestionGraphQL } from '../../../graphql/type';
import {
  type AvailableAnswer,
  type RecommendedQuestion,
  type SelectedQuestion as SelectedQuestionApi,
  QuestionSource,
} from '../../../sdk';
import {
  SelectionType,
  type SelectionTypeView,
  type SimplifiedAnswer,
} from '../../../sdk/models';

import { EMPTY_PREFERRED_ANSWER } from './actions';
import type { Question, QuestionSourceState, State } from './types';

export interface StatefulRecommendedQuestion
  extends Omit<RecommendedQuestionGraphQL, 'source'> {
  source: QuestionSourceState; // override source with custom type for UI state
  isError?: boolean;
  errorText?: string;
  disabled: boolean;
}

export interface SelectedQuestions {
  selectedQuestions: SelectedQuestionApi[];
}

export const getPreferredAnswers = (question?: Question): string[] =>
  question?.preferredAnswers[question.selectionTypeView] ?? [];

const mapPreferredAnswers = (selectedQuestion?: Question) =>
  selectedQuestion
    ? getPreferredAnswers(selectedQuestion)
        .filter((id, index, arr) => {
          if (arr.indexOf(id) === index && id !== '') {
            return id;
          }
        })
        .map((id) => ({ id }))
    : [];

const answerStateMapper =
  (questionState?: Question) =>
  (
    answer: AvailableAnswer | SimplifiedAnswer,
  ): AvailableAnswer | SimplifiedAnswer => ({
    ...answer,
    preferred: questionState
      ? getPreferredAnswers(questionState).includes(answer.id)
      : false,
  });

const isQuestionInError = (questionState?: Question) => {
  if (!questionState?.selected) {
    return false;
  }

  if (questionState.source === QuestionSource.Custom) {
    return false;
  }

  if (getPreferredAnswers(questionState).length === 0) {
    return true;
  }

  if (questionState.selectionType === SelectionType.Range) {
    return (
      new Set(
        getPreferredAnswers(questionState).filter(
          (a) => a !== EMPTY_PREFERRED_ANSWER,
        ),
      ).size !== 2
    );
  }

  return false;
};

export const isVBQInError = (
  state: State,
  isVerifyBoosted: boolean,
): boolean => {
  if (!isVerifyBoosted) {
    return false;
  }

  return (
    getQuestions(state).filter((q) => q.selected && q.certsyVerifiable)
      .length === 0
  );
};

export const questionsInError = (state: State): Question[] =>
  Object.values(state.questions).filter(isQuestionInError);

export const isInErrorState = (state: State): boolean =>
  state.published !== true && questionsInError(state).length > 0;

export const getQuestions = (state: State): StatefulRecommendedQuestion[] =>
  state.showQuestions
    ? Object.values(state.questions).map(questionStateMapper(state))
    : [];

const questionStateMapper =
  (state: State) =>
  (question: Question): StatefulRecommendedQuestion => {
    const mapAnswerPreferredState = answerStateMapper(question);
    const {
      availableAnswerOrders,
      preferredAnswers,
      ...remainingQuestionProps
    } = question;
    return {
      ...remainingQuestionProps,
      isError: isQuestionInError(question),
      availableAnswers:
        question.availableAnswers?.map(mapAnswerPreferredState) ?? [],
      simplifiedAnswers:
        question.simplifiedAnswers?.map(mapAnswerPreferredState) ?? [],
      disabled: libraryQuestionLimitReached(state) && !question.selected,
    };
  };

export const libraryQuestionLimitReached = (state: State): boolean =>
  getAllSelectedQuestions(state.questions).filter(
    (question) => question.source !== QuestionSource.Custom,
  ).length >= state.selectionLimit;

export const getSelectedQuestions = (state: State): SelectedQuestions => {
  const selectedQuestions = getAllSelectedQuestions(state.questions).map(
    (question) => ({
      id: question.id,
      lastInteraction: question.lastInteraction,
      preferredAnswers: mapPreferredAnswers(question),
      essential: question.essential,
    }),
  );

  return {
    selectedQuestions,
  };
};

export const getAllSelectedQuestions = (
  questions: State['questions'],
): Question[] => Object.values(questions).filter((q) => q.selected);

export const getVersionNumber = (state: State): string => {
  if (state.versionNumber === null) {
    throw new Error('Version Number should not be null');
  }

  return state.versionNumber;
};

export const isAnswerSelected =
  (state: State) =>
  (questionId: string, answerId: string): boolean =>
    getPreferredAnswers(state.questions[questionId]).includes(answerId) ||
    false;

export const isQuestionSelected =
  (state: State) =>
  (questionId: string): boolean =>
    state.questions[questionId]?.selected || false;

export const isEssentialSelected =
  (state: State) =>
  (questionId: string): boolean =>
    state.questions[questionId]?.essential || false;

export const getSelectionTypeView =
  (state: State) =>
  (questionId: string): SelectionTypeView =>
    state.questions[questionId]?.selectionTypeView;

type AvailableAnswerOrders = Record<string, number>;

export const getAvailableAnswerOrders = (
  question: RecommendedQuestion | RecommendedQuestionGraphQL,
): AvailableAnswerOrders =>
  question.availableAnswers?.reduce(
    (newArray, aa) => ({ ...newArray, [aa.id]: aa.order }),
    {} as AvailableAnswerOrders,
  ) ?? {};
